import { tr } from '@/lib/i18n-core';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { GROUP_ORDER, groupLabel, type EmrNavId, type EmrSection } from './sections';

function Marker({ section, active }: { section: EmrSection; active: boolean }) {
  if (section.count !== undefined && section.count > 0) {
    return (
      <span
        className={cn(
          'ms-auto min-w-[1.25rem] rounded-full px-1.5 text-center text-[11px] font-semibold tabular',
          active ? 'bg-primary text-primary-foreground' : section.required ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground',
        )}
      >
        {section.count}
      </span>
    );
  }
  if (section.done) {
    return (
      <span className="ms-auto grid h-4 w-4 place-items-center rounded-full bg-success/15 text-success" title={tr('opd', 'emr_marker_done')}>
        <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
        <span className="sr-only">{tr('opd', 'emr_marker_done')}</span>
      </span>
    );
  }
  if (section.required) {
    return (
      <span className="ms-auto inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold text-destructive">
        <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden />
        {tr('opd', 'emr_marker_required')}
      </span>
    );
  }
  return null;
}

function CompactMarker({ section }: { section: EmrSection }) {
  if (section.count !== undefined && section.count > 0) return <span className="tabular opacity-80">{section.count}</span>;
  if (section.done) return <Check className="h-3 w-3 text-success" strokeWidth={3} aria-label={tr('opd', 'emr_marker_done')} />;
  if (section.required) return <span className="h-1.5 w-1.5 rounded-full bg-destructive" role="img" aria-label={tr('opd', 'emr_marker_required')} />;
  return null;
}

export function EmrNav({
  sections,
  active,
  onSelect,
}: {
  sections: EmrSection[];
  active: EmrNavId;
  onSelect: (id: EmrNavId) => void;
}) {
  return (
    <>
      <nav
        aria-label={tr('opd', 'emr_menu')}
        className="hidden overflow-y-auto rounded-2xl border bg-card p-2 shadow-sm lg:sticky lg:top-[calc(var(--emr-bar-top,0px)+var(--emr-bar-h,0px))] lg:block lg:max-h-[max(12rem,calc(var(--emr-scroll-h,100dvh)-var(--emr-bar-h,0px)-0.5rem))]"
      >
        {GROUP_ORDER.map((group) => {
          const items = sections.filter((section) => section.group === group);
          if (items.length === 0) return null;
          return (
            <div key={group} className="mb-1 last:mb-0">
              <p className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">{groupLabel(group)}</p>
              <ul className="space-y-0.5">
                {items.map((section) => {
                  const Icon = section.icon;
                  const selected = section.id === active;
                  return (
                    <li key={section.id}>
                      <button
                        type="button"
                        aria-current={selected ? 'page' : undefined}
                        onClick={() => onSelect(section.id)}
                        className={cn(
                          'group relative flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-start text-sm transition-colors focus-ring',
                          selected ? 'bg-primary/10 font-medium text-primary' : 'text-foreground/80 hover:bg-muted hover:text-foreground',
                        )}
                      >
                        {selected && <span className="absolute inset-y-1.5 start-0 w-[3px] rounded-full bg-primary" aria-hidden />}
                        <span
                          className={cn(
                            'grid h-7 w-7 shrink-0 place-items-center rounded-md transition-colors',
                            selected ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/30' : 'bg-muted/70 text-muted-foreground group-hover:text-foreground',
                          )}
                          aria-hidden
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                        <span className="min-w-0 flex-1 truncate">{section.label}</span>
                        <Marker section={section} active={selected} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      <nav aria-label={tr('opd', 'emr_menu')} className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 lg:hidden">
        {sections.map((section) => {
          const Icon = section.icon;
          const selected = section.id === active;
          return (
            <button
              key={section.id}
              type="button"
              aria-current={selected ? 'page' : undefined}
              onClick={() => onSelect(section.id)}
              className={cn(
                'flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus-ring',
                selected ? 'border-primary bg-primary text-primary-foreground' : 'bg-card text-foreground/80 hover:bg-muted',
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
              {section.label}
              <CompactMarker section={section} />
            </button>
          );
        })}
      </nav>
    </>
  );
}
